import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import BrandLogo from '../BrandLogo';
import ThemeToggle from './ThemeToggle';
import { BETA_COPY, LOGIN_URL, primaryCta } from '../../lib/signup';

const NAV_CTA = primaryCta('nav');

const NAV_LINKS = [
  { label: 'Experience', href: '/' },
  { label: 'How it works', href: '/home#how-it-works' },
  { label: 'About', href: '/about' },
];

export default function SiteNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Smooth-scroll for the "How it works" anchor
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.includes('#')) return;
    const hash = href.split('#')[1];
    e.preventDefault();
    setMobileOpen(false);
    if (location.pathname === '/home') {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.pushState(null, '', href);
    } else {
      navigate('/home');
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  };

  const isActive = (href: string) => !href.includes('#') && location.pathname === href;

  return (
    <header className="sticky top-0 z-50 border-b border-lk-ink/10 bg-paper/95 backdrop-blur-sm">
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-4 md:px-14 md:py-[22px]">
        {/* Logo + pronunciation cue */}
        <div className="flex items-center gap-3.5">
          <Link to="/" aria-label="lykbl home">
            <BrandLogo variant="auto" size="lg" />
          </Link>
          <span className="hidden rounded-full border border-lk-ink/15 px-2.5 py-0.5 text-[11px] font-medium text-lk-ink/60 sm:inline">
            {BETA_COPY.stage}
          </span>
          <span className="hidden border-l border-lk-ink/10 pl-3.5 text-xs text-lk-ink/60 lg:inline">
            say it: &ldquo;likeable&rdquo;
          </span>
        </div>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-8 text-[14.5px] md:flex" aria-label="Main">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className={
                isActive(link.href)
                  ? 'font-semibold text-lk-ink'
                  : 'text-lk-ink/70 transition-colors hover:text-lk-ink'
              }
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden items-center gap-[18px] md:flex">
          <ThemeToggle />
          {/* Sign in lives on the app (lykbl.ai): a quiet link for returning
              users, then one clear CTA — request access (private beta) or join
              the public beta, see lib/signup.ts. */}
          <a
            href={LOGIN_URL}
            className="text-sm font-semibold text-lk-ink/70 transition-colors hover:text-lk-ink"
          >
            Log in
          </a>
          <a
            href={NAV_CTA.href}
            className="rounded-full bg-lk-btn px-5 py-2.5 text-sm font-semibold text-lk-btn-fg transition-opacity hover:opacity-90"
          >
            {NAV_CTA.shortLabel}
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setMobileOpen((open) => !open)}
          className="p-2 text-lk-ink md:hidden"
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-lk-ink/10 bg-paper md:hidden">
          <div className="flex flex-col gap-1 px-5 py-4">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`rounded-xl px-4 py-3 text-lg ${
                  isActive(link.href) ? 'font-semibold text-lk-ink' : 'text-lk-ink/70'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="mt-3 flex flex-col gap-3 border-t border-lk-ink/10 pt-4">
              <div className="flex justify-center">
                <ThemeToggle />
              </div>
              <a
                href={NAV_CTA.href}
                className="rounded-full bg-lk-btn px-5 py-3 text-center text-[15px] font-semibold text-lk-btn-fg"
              >
                {NAV_CTA.shortLabel}
              </a>
              <a
                href={LOGIN_URL}
                className="rounded-full border border-lk-ink/15 px-5 py-3 text-center text-[15px] font-semibold text-lk-ink"
              >
                Log in
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
